import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {
    AddRemoveUserToGroupPayload,
    AttachmentUploadRequest,
    AttachmentUploadResponse,
    BulkAddRemoveUserToGroupPayload,
    ChatUser,
    CommonRecentChat,
    CreateGroupPayload,
    GetChatMessageResponse,
    Group,
    ChatMessage,
    UpdateGroupPayload, StarredMessage, ChatEntityType, GetSavedMessagesPayload, SavedMessageResponse,
    AttachmentThumbnailResponse,
} from "../models/message.model";
import { APIResponse } from "../../../models/Response";
import { AttachmentDownloadInfo } from "../../../models/attachment-download-info";
import { AttachmentDownloadService } from "../../../services/attachment-download.service";
import { Observable, throwError } from "rxjs";
import {
    ActivateDeactivateGroupPayload,
    ATTACHMENT_CONSTANTS,
    DownloadAttachmentParams,
    DownloadResponse, MessageInfoRequest, MessageInfoResponse
} from "../models/conversation.model";
import { catchError, map, switchMap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class ConversationService {
    apiBaseUrl = environment.apiBaseUrl;

    constructor(
        private http: HttpClient,
        private attachmentDownloadService: AttachmentDownloadService,
    ) {}

    /**
     * Fetches all users available for conversation
     * @param accountIds - A string containing account IDs of the logged-in user
     * @param orgId - ID of the organization (optional)
     * @returns Observable containing the list of users
     */
    getAllUsersForConversation(accountIds: string, orgId: number | undefined) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        const path = orgId ? `${this.apiBaseUrl}/users/${orgId}` : `${this.apiBaseUrl}/users/all`;
        return this.http.get<APIResponse<ChatUser[]>>(path, { headers: newHeaders });
    }

    //     /groups/user/{userId}
    getGroupsForUser(userId: number, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.get<APIResponse<Group[]>>(`${this.apiBaseUrl}/groups/user/${userId}`, {
            headers: newHeaders,
        });
    }

    // Get recent messages v2
    getRecentMessagesV2(accountIds: number) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", String(accountIds));
        return this.http.get<APIResponse<CommonRecentChat[]>>(`${this.apiBaseUrl}/chats/all`, {
            headers: newHeaders,
        });
    }

    // Get recent messages v3
    getRecentMessagesV3(accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", String(accountIds));
        return this.http.get<APIResponse<CommonRecentChat[]>>(`${this.apiBaseUrl}/chats/all`, {
            headers: newHeaders,
        });
    }

    //     /groups/create
    createGroup(payload: CreateGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/create`, payload, {
            headers: newHeaders,
        });
    }

    // /api/groups/edit
    editGroup(payload: UpdateGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<String>>(`${this.apiBaseUrl}/groups/edit`, payload, {
            headers: newHeaders,
        });
    }

    //     /groups/removeUser
    removeUserFromGroup(requestPayload: AddRemoveUserToGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/removeUser`, requestPayload, {
            headers: newHeaders,
        });
    }

    getConversationMessagesV3(
        senderId: number,
        receiverId: number,
        messageId: number | null | undefined, // messageId is optional
        accountIds: string,
        direction: number = 0
    ): Observable<APIResponse<GetChatMessageResponse<ChatMessage>>> {
        messageId = messageId != null ? messageId : null; // Set messageId to null if undefined
        let path = `${this.apiBaseUrl}/message/v2/${senderId}/${receiverId}?direction=${direction}`;
        if (messageId != null) {
            path += `&messageId=${messageId}`;
        }
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.get<APIResponse<GetChatMessageResponse<ChatMessage>>>(path, {
            headers: newHeaders,
        });
    }

    getGroupMessagesV3(
        senderId: number,
        receiverId: number,
        messageId: number | null | undefined, // messageId is optional
        accountIds: string,
        direction: number = 0
    ): Observable<APIResponse<GetChatMessageResponse<ChatMessage>>> {
        messageId = messageId != null ? messageId : null; // Set messageId to null if undefined
        let path = `${this.apiBaseUrl}/message/v2/group/${senderId}/${receiverId}?direction=${direction}`;
        if (messageId != null) {
            path += `&messageId=${messageId}`;
        }
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.get<APIResponse<GetChatMessageResponse<ChatMessage>>>(path, {
            headers: newHeaders,
        });
    }

    //api/groups/bulkAddUsers
    bulkAddUsersToGroup(requestPayload: BulkAddRemoveUserToGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/bulkAddUsers`, requestPayload, {
            headers: newHeaders,
        });
    }

    //api/groups/removeAdmin
    removeUserGroupAdmin(requestPayload: AddRemoveUserToGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/removeAdmin`, requestPayload, {
            headers: newHeaders,
        });
    }

    //api/groups/setAdmin
    makeUserGroupAdmin(requestPayload: AddRemoveUserToGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/setAdmin`, requestPayload, {
            headers: newHeaders,
        });
    }

    /**
     * Uploads attachments to the server.
     * @param formData - FormData object containing the files and metadata
     * @return Observable containing the API response
     */
    /**
     * Upload attachments with progress tracking
     */
    uploadAttachments(request: AttachmentUploadRequest): Observable<APIResponse<AttachmentUploadResponse>> {
        // Create FormData
        const formData = this.createFormData(request);
        // Update state to uploading
        const url = `${this.apiBaseUrl}${ATTACHMENT_CONSTANTS.API_ENDPOINTS.UPLOAD_ATTACHMENTS}`;
        return this.http.post<APIResponse<AttachmentUploadResponse>>(url, formData).pipe(
            catchError((error) => {
                return throwError(() => error);
            }),
        );
    }

    /**
     * Downloads an attachment from the backend
     * @param params Download parameters including messageId, messageAttachmentId, and fileName
     * @param headerAccountIds
     * @returns Observable with the file blob and fileName
     */
    /**
     * Downloads a chat attachment (PT-16085 F8). The endpoint now returns download metadata with a
     * presigned S3 URL instead of raw bytes; AttachmentDownloadService turns it into a Blob (plain
     * S3 GET, expired-URL retry, stream-attachment legacy fallback), so callers keep receiving the
     * same DownloadResponse shape as before.
     */
    downloadAttachment(params: DownloadAttachmentParams, headerAccountIds: string): Observable<DownloadResponse> {
        return this.getAttachmentDownloadInfo(params, headerAccountIds).pipe(
            switchMap((info) =>
                this.attachmentDownloadService.fetchBlob(
                    info,
                    () => this.streamAttachment(params, headerAccountIds),
                    () => this.getAttachmentDownloadInfo(params, headerAccountIds),
                ),
            ),
            map((blob) => {
                return {
                    data: blob,
                    fileName: params.fileName,
                };
            }),
            catchError((error) => {
                return throwError(() => error);
            }),
        );
    }

    /** Fetches the download metadata {fileName, fileType, fileSize, downloadUrl} for one chat attachment. */
    getAttachmentDownloadInfo(params: DownloadAttachmentParams, headerAccountIds: string): Observable<AttachmentDownloadInfo> {
        const url = `${this.apiBaseUrl}${ATTACHMENT_CONSTANTS.API_ENDPOINTS.DOWNLOAD_ATTACHMENT}`;
        return this.http
            .get<APIResponse<AttachmentDownloadInfo>>(url, {
                params: this.attachmentParams(params),
                headers: new HttpHeaders({ AccountIds: headerAccountIds }),
            })
            .pipe(map((response) => response.data));
    }

    /**
     * Presigned S3 thumbnails for the given attachment ids (PT-16085 F8); thumbnailUrl is null for
     * legacy rows (their base64 comes in fileContent) and for types without a preview.
     */
    getAttachmentThumbnails(attachmentIds: number[], headerAccountIds: string): Observable<AttachmentThumbnailResponse[]> {
        const url = `${this.apiBaseUrl}${ATTACHMENT_CONSTANTS.API_ENDPOINTS.DOWNLOAD_THUMBNAILS}`;
        const httpParams = new HttpParams().set("attachmentIds", attachmentIds.join(","));
        return this.http
            .get<APIResponse<AttachmentThumbnailResponse[]>>(url, {
                params: httpParams,
                headers: new HttpHeaders({ AccountIds: headerAccountIds }),
            })
            .pipe(map((response) => response.data ?? []));
    }

    /**
     * Legacy fallback (PT-16085 G3): streams the bytes of an attachment still stored in the
     * database. Only called when downloadUrl is null; removed together with backend P16.
     */
    private streamAttachment(params: DownloadAttachmentParams, headerAccountIds: string): Observable<Blob> {
        const url = `${this.apiBaseUrl}${ATTACHMENT_CONSTANTS.API_ENDPOINTS.STREAM_ATTACHMENT}`;
        return this.http.get(url, {
            params: this.attachmentParams(params),
            responseType: "blob",
            headers: new HttpHeaders({ AccountIds: headerAccountIds }),
        });
    }

    private attachmentParams(params: DownloadAttachmentParams): HttpParams {
        return new HttpParams()
            .set("messageId", params.messageId.toString())
            .set("messageAttachmentId", params.messageAttachmentId.toString())
            .set("fileName", params.fileName);
    }

    /**
     * Create FormData from request
     */
    private createFormData(request: AttachmentUploadRequest): FormData {
        const formData = new FormData();
        // Add files
        request.files.forEach((file) => {
            formData.append("files", file);
        });
        // Add uploader account ID
        formData.append("uploaderAccountId", request.uploaderAccountId.toString());
        // Add message as JSON string
        formData.append("message", JSON.stringify(request.message));
        return formData;
    }

    /** Activate or deactivate a group
     * @param activateDeactivateGroupPayload
     * @param accountIds
     * @return Observable with API response<Group>
     */
    activateDeactivateGroup(activateDeactivateGroupPayload: ActivateDeactivateGroupPayload, accountIds: string) {
        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);
        return this.http.post<APIResponse<Group>>(`${this.apiBaseUrl}/groups/activeInactiveGroup`, activateDeactivateGroupPayload, {
            headers: newHeaders,
        });
    }

    // Get Message Info
    getMessageInfo(chat: ChatMessage, accountIds: string) {
        const payload: MessageInfoRequest = {
            messageId: chat.messageId,
            senderId: chat.senderId,
            ...(chat.groupId
                    ? { groupId: chat.groupId }
                    : { receiverId: chat.receiverId }
            )
        };

        let newHeaders = new HttpHeaders();
        newHeaders = newHeaders.append("AccountIds", accountIds);

        return this.http.post<APIResponse<MessageInfoResponse>>(
            `${this.apiBaseUrl}/message/getMessageInfo`,
            payload,
            { headers: newHeaders }
        );
    }

    private buildStarHeaders(accountId: string): HttpHeaders {
        let headers = new HttpHeaders();
        headers = headers.append('accountIds', accountId);
        headers = headers.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
        headers = headers.append('screenname', accountId);
        return headers;
    }

    starMessage(
        accountId: string,
        messageId: number,
        chatEntityType: ChatEntityType,
        chatEntityId: number
    ): Observable<APIResponse<StarredMessage>> {
        const headers = this.buildStarHeaders(accountId);
        const payload = {
            accountId: parseInt(accountId),
            messageId,
            chatEntityType,
            chatEntityId,
        };
        return this.http.post<APIResponse<StarredMessage>>(
            `${this.apiBaseUrl}/message/star`, payload, { headers }
        );
    }

    unstarMessage(
        accountId: string,
        messageId: number,
        chatEntityType: ChatEntityType,
        chatEntityId: number
    ): Observable<APIResponse<StarredMessage>> {
        const headers = this.buildStarHeaders(accountId);
        const payload = {
            accountId: parseInt(accountId),
            messageId,
            chatEntityType,
            chatEntityId,
            delete: true,
        };
        return this.http.post<APIResponse<StarredMessage>>(
            `${this.apiBaseUrl}/message/star`, payload, { headers }
        );
    }

    getStarredMessages(accountId: string): Observable<APIResponse<StarredMessage[]>> {
        const headers = this.buildStarHeaders(accountId);
        return this.http.post<APIResponse<StarredMessage[]>>(
            `${this.apiBaseUrl}/message/getAllStarredMessage`, null, { headers }
        );
    }

    getMessageByMessageIds(messageIds: string, accountId: string): Observable<APIResponse<ChatMessage[]>> {
        let headers = new HttpHeaders();
        headers = headers.append('AccountIds', accountId);
        return this.http.post<APIResponse<ChatMessage[]>>(
            `${this.apiBaseUrl}/message/getMessageByMessageIds?messageIds=${messageIds}`,
            { headers }
        );
    }

    getSavedMessages(payload: GetSavedMessagesPayload, accountId: string): Observable<APIResponse<SavedMessageResponse[]>> {
        let headers = new HttpHeaders();
        headers = headers.append('AccountIds', accountId);
        return this.http.post<APIResponse<SavedMessageResponse[]>>(
            `${this.apiBaseUrl}/message/saved`, payload,
            { headers }
        );
    }
}
